import React from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useAuthLayout } from '@contexts/layout';
import { isLogoOnlyRoute } from '@/constants/layoutRoutes';

interface IRouteTransitionProps {
  children: React.ReactNode;
}

interface PageMotion {
  crossesShell: boolean;
  instant: boolean;
}

const pageVariants = {
  initial: { opacity: 0, y: 0 },
  in: ({ instant }: PageMotion) => ({
    opacity: 1,
    y: 0,
    transition: instant
      ? { duration: 0 }
      : { type: 'tween' as const, ease: 'easeInOut' as const, duration: 0.3 },
  }),
  // The shell gains or loses the nav and header the moment the route changes, so a page
  // still fading out would be reflowed into the other shell and visibly jump.
  out: ({ crossesShell }: PageMotion) =>
    crossesShell
      ? { opacity: 0, transition: { duration: 0 } }
      : {
          opacity: 0,
          y: 15,
          transition: { type: 'tween' as const, ease: 'easeIn' as const, duration: 0.2 },
        },
};

/**
 * Crossfades routed pages. A page that crosses between the logo-only and full shells stays
 * invisible, at its final position, until the shell has settled, then fades in.
 */
const RouteTransition: React.FC<IRouteTransitionProps> = ({ children }) => {
  const location = useLocation();
  const isShellSettling = useAuthLayout()?.isShellSettling ?? false;
  const reduceMotion = useReducedMotion() ?? false;

  const [shellChange, setShellChange] = React.useState({
    pathname: location.pathname,
    crossesShell: false,
  });
  if (shellChange.pathname !== location.pathname) {
    setShellChange({
      pathname: location.pathname,
      crossesShell: isLogoOnlyRoute(shellChange.pathname) !== isLogoOnlyRoute(location.pathname),
    });
  }

  const { crossesShell } = shellChange;
  const motionCustom: PageMotion = { crossesShell, instant: crossesShell && reduceMotion };
  const holdForShell = crossesShell && !reduceMotion && isShellSettling;

  return (
    <AnimatePresence mode="wait" custom={motionCustom}>
      <motion.div
        key={location.pathname}
        custom={motionCustom}
        initial="initial"
        animate={holdForShell ? 'initial' : 'in'}
        exit="out"
        variants={pageVariants}
        style={{ width: '100%', height: '100%' }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

export default RouteTransition;
